// -----------------------------------------------------------------------------
//  Mini–propuesta / recibo solar
// -----------------------------------------------------------------------------
import React, {
  forwardRef,
  useEffect,
  useRef,
  useImperativeHandle,
} from "react";
import html2pdf from "html2pdf.js";
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Title,
  Tooltip,
} from "chart.js";
import logo from "../../assets/logo/logo-oficial.png";

// — registrar controladores una sola vez —
Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Title,
  Tooltip
);

const QuotePdf = forwardRef(function QuotePdf(
  { data, hideActions = false },
  ref
) {
  const pdfRef = useRef(null);
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  /* ░░░ INJECT GLOBAL STYLES ░░░ */
  useEffect(() => {
    if (document.getElementById("kwsolar-propuesta-styles")) return;
    const tag = document.createElement("style");
    tag.id = "kwsolar-propuesta-styles";
    tag.innerHTML = `
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700;800&display=swap');
      body { font-family: 'Inter', sans-serif; }

      .container { max-width: 900px; margin: 2rem auto; padding: 2.5rem;
        background: #fff; border-radius: 16px;
        box-shadow: 0 15px 40px rgba(0,0,0,.15); }

      .header { display: flex; flex-direction: column; align-items: center;
        justify-content: center; text-align: center; gap: .75rem; margin-bottom: 2rem; }

      .section-title { font-size: 2rem; font-weight: 700; color: #E06A00;
        margin: 1.8rem 0 .8rem; border-bottom: 2px solid #E06A00; text-align: center; }

      .cards-grid { display: grid; gap: 2rem; margin-bottom: 1.5rem; }

      .card { background: #fefefe; border-radius: 10px; padding: 2rem;
        box-shadow: 0 6px 15px rgba(0,0,0,.08);
        display: flex; flex-direction: column; gap: 1rem;
        transition: .2s transform, .2s box-shadow; }
      .card:hover { transform: translateY(-5px);
        box-shadow: 0 10px 25px rgba(0,0,0,.12); }
      .card-icon  { font-size: 2.5rem; color: #E06A00; text-align: center; }
      .card-value { font-size: 2.2rem; font-weight: 800; color: #CC5500; text-align: center; }
      .card-label { font-size: 1.1rem; color: #666; font-style: italic; text-align: center; }

      .chart-box { position: relative; height: 400px; width: 100%; margin: 2.5rem 0;
        background: #fefefe; border-radius: 10px;
        box-shadow: 0 6px 15px rgba(0,0,0,.08); padding: 1.5rem; }

      .cta-btn { background: #E06A00; color: #fff; padding: 1rem 2.5rem;
        border-radius: 10px; font-weight: 700;
        box-shadow: 0 5px 15px rgba(224,106,0,.4); transition: .3s; }
      .cta-btn:hover { background: #CC5500; transform: translateY(-3px); }

      .disclaimer { font-size: .85rem; color: #777; text-align: center; margin-top: 2rem;
        padding-top: 1rem; border-top: 1px dashed #e0e0e0; }`;
    document.head.appendChild(tag);
  }, []);

  /* ░░░ CHART ░░░ */
  useEffect(() => {
    if (!data || !canvasRef.current) return;

    chartRef.current?.destroy(); // limpia
    chartRef.current = new Chart(canvasRef.current.getContext("2d"), {
      type: "bar",
      data: {
        labels: [
          "Ene",
          "Feb",
          "Mar",
          "Abr",
          "May",
          "Jun",
          "Jul",
          "Ago",
          "Sep",
          "Oct",
          "Nov",
          "Dic",
        ],
        datasets: [
          {
            label: "Consumo CFE (kWh)",
            data: data.monthlyConsumption || [],
            backgroundColor: "#9CA3AF", // gris
            borderColor: "#9CA3AF",
            borderRadius: 5,
          },
          {
            label: "Producción Solar (kWh)",
            data: data.monthlyProduction || [],
            backgroundColor: "#F7931E", // naranja
            borderColor: "#F7931E",
            borderRadius: 5,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: "Comparativa Mensual: Consumo vs. Producción Solar",
            font: { size: 18, weight: "600" },
            color: "#333",
          },
          legend: { position: "top" },
          tooltip: { enabled: true },
        },
        scales: {
          y: { beginAtZero: true, title: { display: true, text: "kWh" } },
        },
      },
    });

    return () => chartRef.current?.destroy();
  }, [data]);

  /* ░░░ PDF ░░░ */
  const downloadPDF = () =>
    html2pdf()
      .from(pdfRef.current)
      .set({
        margin: 0,
        filename: `Propuesta-KWSOLAR-${Date.now()}.pdf`,
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4" },
      })
      .save();

  /*  ░░░ exponer método download() al padre ░░░ */
  useImperativeHandle(ref, () => ({ download: downloadPDF }));

  if (!data) return null;

  const fmt = (v, { currency = false } = {}) =>
    v == null
      ? "-"
      : Number(v).toLocaleString(
          "es-MX",
          currency ? { style: "currency", currency: "MXN" } : {}
        );

  /* ░░░ RENDER ░░░ */
  return (
    <div className="container" ref={pdfRef}>
      {/* HEADER */}
      <div className="header">
        <img src={logo} alt="KWSOLAR" style={{ maxWidth: 200 }} />
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
          ¡Transforma tu Energía,
          <br /> Transfórmala con Nosotros!
        </h1>
      </div>

      {/* …—— Secciones ——… */}
      <Section title="Tu Consumo Actual y Nuestro Diagnóstico" columns={3}>
        <Card icon="⚡" label="Tipo de Tarifa CFE" value={data.tarifaCFE} />
        <Card
          icon="📈"
          label="Consumo (kWh/mes)"
          value={fmt(data.consumoTotalKwh)}
        />
        <Card
          icon="💸"
          label="Pago (MXN/mes)"
          value={fmt(data.pagoActualMxn, { currency: true })}
        />
      </Section>

      <Section title="Tu Futuro con KWSOLAR: El Sistema Propuesto" columns={3}>
        <Card
          icon="☀️"
          label="Potencia Req. (kWh/mes)"
          value={fmt(data.potenciaSistema)}
        />
        <Card icon="🏡" label="Nº Paneles" value={data.numPaneles} />
        <Card icon="🔋" label="Potencia/Panel (W)" value={data.potenciaPanel} />
        <Card
          icon="📊"
          label="Producción (kWh/mes)"
          value={fmt(data.produccionSistema)}
        />
        <Card
          icon="💰"
          label="Ahorro (MXN/mes)"
          value={fmt(data.ahorroEstimado, { currency: true })}
        />
      </Section>

      {/* GRÁFICA */}
      <h2 className="section-title">
        Tu Impacto: Consumo vs. Producción Solar
      </h2>
      <div className="chart-box">
        <canvas ref={canvasRef} />
      </div>

      {/* INVERSIÓN */}
      <Section title="Una Inversión Inteligente para tu Patrimonio" columns={2}>
        <Card icon="💲" label="Inversión (USD)" value={data.precioUSD} />
        <Card icon="🇲🇽" label="Inversión (MXN)" value={data.precioMXN} />
      </Section>

      {/* Retorno */}
      <p className="text-center text-lg text-gray-700 mb-10">
        Con este sistema, no solo estás invirtiendo en un futuro sostenible,
        sino que también asegurarás un&nbsp;
        <span className="font-bold text-[#E06A00]">
          retorno de inversión estimado de&nbsp;
          {data.retornoAnios || "3 – 4"} años
        </span>
        . ¡Después la energía será prácticamente gratis!
      </p>

      {/* DISCLAIMER */}
      <p className="disclaimer">
        *Esta propuesta está sujeta a verificación en sitio y tipo de cambio.
        Los datos son estimaciones y pueden variar.
      </p>

      {/* botón interno opcional */}
      {!hideActions && (
        <div className="text-center mt-8">
          <button onClick={downloadPDF} className="cta-btn">
            Descargar PDF
          </button>
        </div>
      )}
    </div>
  );
});

/* ---------- helpers ---------- */
const Section = ({ title, columns = 3, children }) => (
  <>
    <h2 className="section-title">{title}</h2>
    <div
      className="cards-grid"
      style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
    >
      {React.Children.toArray(children)}
    </div>
  </>
);

const Card = ({ icon, label, value }) => (
  <div className="card">
    <div className="card-icon">{icon}</div>
    <span className="card-label">{label}</span>
    <span className="card-value">{value}</span>
  </div>
);

export default QuotePdf;
